/**
 * The loyalty API client, built once per page.
 *
 * Requests go straight from the browser to the 3Halves gateway — there is no WordPress proxy in
 * front of them. That is a per-origin fact rather than a property of the API: the gateway keeps a
 * CORS allowlist, and an origin that is not on it fails the preflight, which no token can fix.
 * See docs/gotchas.md.
 */

import { LoyaltyAPI, withCoalescedFetch } from '@drewling/urc-loyalty-api';
import { PREFETCH_CACHE, clearTiersCache, withPrefetchCache } from '@drewling/urc-loyalty-ui';
import { accessToken, isSignedIn } from './urcAuth';
import { CONSENT_EXEMPT, withConsentGate } from './consentGate';
import { isLoyaltyConsentGranted } from './consent';
import { createBadgeArtworkStore } from './badgeArtwork';

/** Methods whose data a hover can warm before the modal that reads it opens. */
const PREFETCH_METHODS = [
	'getChallenges',
	'getBadgesDetail',
	'getLeaderboardDetail',
	'getProfileStats',
];

let client = null;

/** Whether the tier ladder has already been dropped once on this page. See getClient(). */
let tiersCleared = false;

/** Where badge artwork is kept between page loads. Built once, and outlives resetClient(). */
let artworkStore = null;

/**
 * One client for every block on the page. Sharing it is not a tidiness point: several methods
 * fetch /api/users/me below the method surface, and `withCoalescedFetch` can only collapse those
 * into one request if the blocks issue them through the same fetch impl.
 *
 * @return {import('@drewling/urc-loyalty-api').ILoyaltyAPI} The shared client.
 */
export function getClient() {
	if ( client ) {
		return client;
	}

	// useTiers memoises the tier ladder in sessionStorage with NO TTL, so nothing else would ever
	// drop it. A fresh page load is the one moment worth doing so.
	//
	// **Corrected 2026-09-07: the ladder is not per-fan, and this comment used to say it was.**
	// `getTiers` is `auth: 'public'` in @drewling/urc-loyalty-api -- `requestPublic()`, no
	// Authorization header at all, which is also why ./consentGate.js exempts it -- so every fan
	// and every anonymous visitor gets the same ladder and one fan cannot be served another's. What
	// this clear is actually for is the case the library's own docblock names second: the
	// ENVIRONMENT behind the client changing, i.e. a deploy repointing baseUrl at a gateway with a
	// different ladder, against a tab whose sessionStorage outlives it.
	//
	// It follows that an account switch does NOT need this, which is why the guard below is right
	// and why resetClient() deliberately does not repeat it. Re-clearing on a switch would buy one
	// extra /api/tiers and nothing else.
	if ( ! tiersCleared ) {
		tiersCleared = true;
		clearTiersCache();
	}

	const baseUrl = ( window.urcLoyalty && window.urcLoyalty.baseUrl ) || '';

	// Module-level rather than per client, so the rebuild resetClient() exists for keeps it. The
	// artwork is the programme's, not the fan's, and a snapshot names the origin it came from -- so
	// an account switch has nothing in it to protect, and dropping it would only buy the next fan a
	// fresh ~1 MB download. See ./badgeArtwork.js and D-095.
	if ( ! artworkStore ) {
		artworkStore = createBadgeArtworkStore( { key: baseUrl, now: () => Date.now() } );
	}

	const raw = new LoyaltyAPI( {
		// A provider, not a string: the Auth0 SDK can replace the token under the page -- an
		// expiry, a sign-out, a sign-in as somebody else -- and this is called before every
		// request, so none of those needs a client rebuild. It resolves '' rather than throwing
		// when there is none, which is what routes the public tier ladder through and refuses a
		// self-scoped read before the wire.
		accessToken,
		baseUrl,
		// `scope` became required at @drewling/urc-loyalty-api 0.15.0, and it names WHICH
		// CREDENTIAL the collapsed requests carry: the coalescer keys on `scope` plus the URL,
		// because a `typeof fetch` seam cannot read the Authorization header portably.
		//
		// **A constant, not the fan, and the reason is that the fan would be a lie here.** The
		// in-flight map is per closure and this coalescer is built inside getClient(), so one
		// client is one coalescer and no two identities could share a map whatever this said.
		// A fan-derived scope would also be read at the wrong moment: getClient() runs before the
		// Auth0 SDK settles, so authState() answers null on an ordinary load and the scope would
		// read "anonymous" on a client that goes on to carry the fan's bearer for the rest of the
		// page. Nothing would correct it -- resetClient() fires on an account switch and NOT on a
		// credential arriving, which is D-079's cue and was chosen deliberately.
		//
		// Nothing leaks through that gap. The one route that goes out with no Authorization header
		// is the public /api/tiers; every self-scoped read is refused before the wire while
		// `accessToken` resolves ''. An anonymous and an authenticated request are never the same
		// in-flight GET. See D-087.
		//
		// The literal must not begin `urc.` -- perFanStorage.test.js greps this bundle for
		// `urc.*` string literals and requires each to be fan-scoped or on the reset list.
		//
		// **Do not hoist this call out of getClient().** The same release added a runtime claim:
		// a LoyaltyAPI refuses a coalescer another LoyaltyAPI already holds. A module-level
		// coalescer would build the first client and then throw on the rebuild resetClient()
		// exists to allow -- i.e. it would fail only on an account switch.
		fetch: withCoalescedFetch( globalThis.fetch.bind( globalThis ), {
			scope: 'sp-starter-theme',
		} ),
		timeoutMs: 10_000,
		// Opts getBadgesDetail() into badge ARTWORK (`imageUrl` / `lockedImageUrl`). On a store miss
		// it also reads the ~1 MB artwork leg; on a hit only the plain catalogue. Which picture a
		// surface draws is ./badgeArtwork.js's resolveBadgeIcon, passed to each surface.
		badgeArtworkStore: artworkStore,
	} );

	/*
	 * The consent gate goes OUTSIDE the prefetch cache, and the order is load-bearing twice over: a
	 * refusal never reaches that cache to be stored, and data fetched while consent was granted
	 * cannot be served out of it after consent is withdrawn. `withCoalescedFetch` is unaffected --
	 * it sits at the fetch level, below LoyaltyAPI.
	 *
	 * This is the only `new LoyaltyAPI(` in the theme, so wrapping it here is a structural
	 * guarantee rather than a rule to remember at each call site: the toast gate, the notification
	 * poll, all four blocks, useJoinFunnel and the library's own useTiers all go through it.
	 *
	 * `hasCredential` asks whether the fan is SIGNED IN rather than whether a token is in hand, and
	 * passing a credentialless browser straight through is required rather than an optimisation --
	 * `getTiers` is public and must keep working for a signed-out visitor, and an anonymous page
	 * load must not buy a doomed consent read. See ./consentGate.js.
	 *
	 * **It is deliberately the cheaper question, and the gap it opens is closed one layer down.**
	 * `isSignedIn()` is synchronous and answers false until the SDK settles, so a read issued in
	 * those first few hundred milliseconds is not gated. Nothing fan-bearing can escape through it:
	 * the same unsettled state makes `accessToken` above resolve '', so the library refuses every
	 * self-scoped route before the wire and the only requests that go out are the public ones,
	 * which carry no identity for a consent gate to protect. Asking for a token here instead would
	 * make the predicate async for no gain.
	 */
	client = withConsentGate(
		withPrefetchCache( raw, {
			methods: PREFETCH_METHODS,
			ttlMs: 30_000,
			now: () => Date.now(),
		} ),
		{
			isGranted: isLoyaltyConsentGranted,
			hasCredential: isSignedIn,
			exempt: CONSENT_EXEMPT,
		}
	);

	return client;
}

/**
 * Throw the shared client away, so the next getClient() builds one with a NEW identity.
 *
 * That identity is the mechanism, not a side effect. `useLoyaltyData` in @drewling/urc-loyalty-ui
 * keys its effect on `[client, attempt, hasOverride]` and ./useJoinFunnel.js on
 * `[client, signedIn]`, so handing the tree a different client object is the one supported way to
 * make every block fetch again. It is also the ONLY way out of a `LoyaltyAuthError` whose
 * `requiresReauth` is true, because that state comes back with `retry: null` and renders no
 * control at all.
 *
 * Called from exactly one place -- ../loyalty.jsx, when a credential turns up on a page that
 * started without one (D-066). Not a general-purpose escape hatch: a rebuild drops the prefetch
 * cache and re-triggers every mounted block's reads, which is wanted on a page whose blocks are
 * all showing "Session expired" and wasteful anywhere else.
 *
 * An in-flight call is unaffected -- it holds the old client in its closure -- so a join already
 * being retried keeps its whole retry budget.
 *
 * @return {void}
 */
export function resetClient() {
	client = null;
}

/**
 * Drop the page's memoised fan reads, after the fan's membership changed underneath them.
 *
 * The prefetch memo is keyed by method name with no fan in it (D-075), so a fan who joins or leaves
 * inside one document would otherwise be served the reads made before -- a fast rejoin would read
 * "not a member" out of a memo written seconds earlier. Called by ../membership/index.js on every
 * commit and supersede. A no-op before the client exists.
 *
 * @return {void}
 */
export function invalidateFanReads() {
	if ( ! client ) {
		return;
	}

	PREFETCH_METHODS.forEach( method => client[ PREFETCH_CACHE ]?.invalidate( method ) );
}
